import React, { useState } from "react";
import {
    Calendar,
    Check,
    ChevronRight,
    CreditCard,
    Lock,
    Mail,
    Phone,
    Shield,
    User,
} from "lucide-react";
import styles from "./HotelBookingWidget.module.scss";
import { useProfile } from "../context/ProfileContext";

type HotelPrice = {
    currency?: string;
    total?: string;
};

type HotelOffer = {
    checkInDate?: string;
    checkOutDate?: string;
    room?: {
        typeEstimated?: {
            category?: string;
        };
    };
    price?: HotelPrice;
};

type HotelBookingData = {
    hotel?: {
        name?: string;
        image?: string;
    };
    offers?: HotelOffer[];
};

type TravelerDetails = {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    dob: string;
    paymentMethod: "card" | "applepay" | "googlepay" | "paypal";
};

type HotelBookingWidgetProps = {
    booking: HotelBookingData;
    onComplete?: (details: TravelerDetails) => void;
    onCancel?: () => void;
    onViewBookings?: () => void;
};

const STEPS = ["Summary", "Details", "Payment", "Confirmed"] as const;

export const HotelBookingWidget: React.FC<HotelBookingWidgetProps> = ({
    booking,
    onComplete,
    onCancel,
    onViewBookings,
}) => {
    const { addBooking } = useProfile();
    const [step, setStep] = useState(0);
    const [processing, setProcessing] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState<TravelerDetails["paymentMethod"]>("card");
    const [formData, setFormData] = useState<Omit<TravelerDetails, "paymentMethod">>({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        dob: "",
    });

    const offer = booking?.offers?.[0];
    const price = offer?.price;
    const bookingName = booking?.hotel?.name || "Hotel";
    const canProceedDetails = Boolean(formData.firstName && formData.lastName && formData.email);

    const handleInput = (field: keyof Omit<TravelerDetails, "paymentMethod">, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handlePayment = async () => {
        setProcessing(true);
        await new Promise((resolve) => window.setTimeout(resolve, 1500));
        setProcessing(false);
        setStep(3);

        const travellerName = [formData.firstName, formData.lastName].filter(Boolean).join(" ");
        const startDate = offer?.checkInDate ? new Date(offer.checkInDate).toISOString() : new Date().toISOString();
        const endDate = offer?.checkOutDate ? new Date(offer.checkOutDate).toISOString() : startDate;

        addBooking({
            kind: "hotel",
            title: bookingName,
            subtitle: travellerName || undefined,
            startDate,
            endDate,
            amount: parseFloat(price?.total || "0") || 0,
            currency: price?.currency || "USD",
            status: "confirmed",
            reference: `HB-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
            destination: bookingName,
            image: booking?.hotel?.image,
        });

        onComplete?.({ ...formData, paymentMethod });
    };

    return (
        <div className={styles.widget}>
            <div className={styles.steps}>
                {STEPS.map((label, index) => (
                    <div
                        key={label}
                        className={`${styles.step} ${index <= step ? styles.stepActive : ""} ${index < step ? styles.stepDone : ""}`}
                    >
                        <span className={styles.stepDot}>{index < step ? <Check size={10} /> : index + 1}</span>
                        <span className={styles.stepLabel}>{label}</span>
                    </div>
                ))}
            </div>

            {step === 0 && (
                <div className={styles.body}>
                    <div className={styles.summaryCard}>
                        <div className={styles.summaryType}>Hotel Booking</div>
                        <h3 className={styles.summaryName}>{bookingName}</h3>

                        <div className={styles.summaryDetails}>
                            <span>Check-in: {offer?.checkInDate || "-"}</span>
                            <span>Check-out: {offer?.checkOutDate || "-"}</span>
                            {offer?.room?.typeEstimated?.category && (
                                <span>Room: {offer.room.typeEstimated.category.replace(/_/g, " ")}</span>
                            )}
                        </div>

                        <div className={styles.summaryPrice}>
                            <span>Total</span>
                            <strong>
                                {price?.currency || "USD"} {price?.total || "-"}
                            </strong>
                        </div>
                    </div>

                    <div className={styles.actions}>
                        <button className={styles.btnSecondary} onClick={onCancel}>
                            Cancel
                        </button>
                        <button className={styles.btnPrimary} onClick={() => setStep(1)}>
                            Continue <ChevronRight size={14} />
                        </button>
                    </div>
                </div>
            )}

            {step === 1 && (
                <div className={styles.body}>
                    <div className={styles.form}>
                        <div className={styles.formRow}>
                            <div className={styles.field}>
                                <label>
                                    <User size={12} /> First Name
                                </label>
                                <input
                                    type="text"
                                    value={formData.firstName}
                                    onChange={(e) => handleInput("firstName", e.target.value)}
                                    placeholder="John"
                                />
                            </div>
                            <div className={styles.field}>
                                <label>
                                    <User size={12} /> Last Name
                                </label>
                                <input
                                    type="text"
                                    value={formData.lastName}
                                    onChange={(e) => handleInput("lastName", e.target.value)}
                                    placeholder="Doe"
                                />
                            </div>
                        </div>

                        <div className={styles.field}>
                            <label>
                                <Mail size={12} /> Email
                            </label>
                            <input
                                type="email"
                                value={formData.email}
                                onChange={(e) => handleInput("email", e.target.value)}
                                placeholder="john@example.com"
                            />
                        </div>

                        <div className={styles.formRow}>
                            <div className={styles.field}>
                                <label>
                                    <Phone size={12} /> Phone
                                </label>
                                <input
                                    type="tel"
                                    value={formData.phone}
                                    onChange={(e) => handleInput("phone", e.target.value)}
                                    placeholder="+1 234 567 890"
                                />
                            </div>
                            <div className={styles.field}>
                                <label>
                                    <Calendar size={12} /> Date of Birth
                                </label>
                                <input
                                    type="date"
                                    value={formData.dob}
                                    onChange={(e) => handleInput("dob", e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    <div className={styles.actions}>
                        <button className={styles.btnSecondary} onClick={() => setStep(0)}>
                            Back
                        </button>
                        <button className={styles.btnPrimary} disabled={!canProceedDetails} onClick={() => setStep(2)}>
                            Continue to Payment <ChevronRight size={14} />
                        </button>
                    </div>
                </div>
            )}

            {step === 2 && (
                <div className={styles.body}>
                    <div className={styles.paymentMethods}>
                        <button
                            className={`${styles.pm} ${paymentMethod === "card" ? styles.pmActive : ""}`}
                            onClick={() => setPaymentMethod("card")}
                        >
                            <CreditCard size={16} /> Card
                        </button>
                        <button
                            className={`${styles.pm} ${paymentMethod === "applepay" ? styles.pmActive : ""}`}
                            onClick={() => setPaymentMethod("applepay")}
                        >
                            Apple Pay
                        </button>
                        <button
                            className={`${styles.pm} ${paymentMethod === "googlepay" ? styles.pmActive : ""}`}
                            onClick={() => setPaymentMethod("googlepay")}
                        >
                            Google Pay
                        </button>
                        <button
                            className={`${styles.pm} ${paymentMethod === "paypal" ? styles.pmActive : ""}`}
                            onClick={() => setPaymentMethod("paypal")}
                        >
                            PayPal
                        </button>
                    </div>

                    {paymentMethod === "card" && (
                        <div className={styles.form}>
                            <div className={styles.field}>
                                <label>
                                    <CreditCard size={12} /> Card Number
                                </label>
                                <input type="text" placeholder="4242 4242 4242 4242" maxLength={19} />
                            </div>
                            <div className={styles.formRow}>
                                <div className={styles.field}>
                                    <label>Expiry</label>
                                    <input type="text" placeholder="MM/YY" maxLength={5} />
                                </div>
                                <div className={styles.field}>
                                    <label>
                                        <Lock size={12} /> CVC
                                    </label>
                                    <input type="text" placeholder="123" maxLength={4} />
                                </div>
                            </div>
                        </div>
                    )}

                    {paymentMethod !== "card" && (
                        <div className={styles.expressInfo}>
                            <p>
                                You will be redirected to {paymentMethod === "applepay" ? "Apple Pay" : paymentMethod === "googlepay" ? "Google Pay" : "PayPal"} to complete payment.
                            </p>
                        </div>
                    )}

                    <div className={styles.secure}>
                        <Shield size={12} />
                        <span>Processed through NoETL playbooks. Your session is protected by the gateway.</span>
                    </div>

                    <div className={styles.paymentTotal}>
                        <span>Total to pay</span>
                        <strong>
                            {price?.currency || "USD"} {price?.total || "-"}
                        </strong>
                    </div>

                    <div className={styles.actions}>
                        <button className={styles.btnSecondary} onClick={() => setStep(1)}>
                            Back
                        </button>
                        <button className={`${styles.btnPrimary} ${styles.btnPay}`} onClick={handlePayment} disabled={processing}>
                            {processing ? "Processing..." : `Pay ${price?.currency || "USD"} ${price?.total || "-"}`}
                        </button>
                    </div>
                </div>
            )}

            {step === 3 && (
                <div className={`${styles.body} ${styles.confirmed}`}>
                    <div className={styles.confirmIcon}>
                        <Check size={28} />
                    </div>
                    <h3>Thank you for booking!</h3>
                    <p>
                        Your hotel has been confirmed. You will receive a confirmation email at <strong>{formData.email}</strong>.
                    </p>
                    <div className={styles.confirmSummary}>
                        <div className={styles.confirmRow}>
                            <span>Booking</span>
                            <strong>{bookingName}</strong>
                        </div>
                        <div className={styles.confirmRow}>
                            <span>Amount</span>
                            <strong>
                                {price?.currency || "USD"} {price?.total || "-"}
                            </strong>
                        </div>
                        <div className={styles.confirmRow}>
                            <span>Guest</span>
                            <strong>
                                {formData.firstName} {formData.lastName}
                            </strong>
                        </div>
                    </div>

                    <div className={`${styles.actions} ${styles.actionsConfirm}`}>
                        <button className={styles.btnPrimary} onClick={() => onViewBookings?.()}>
                            View my bookings
                        </button>
                        <button className={styles.btnSecondary} onClick={onCancel}>
                            Book another trip
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

